/* candy-wishlist.js — the wishlist store. One file, every page, one source of truth.

   ============================ WHY IT IS BUILT THIS WAY ============================
   The brief was "100% working on localStorage + login switch". Both halves are here, and
   the second one needs explaining, because there is a limit worth writing down rather
   than discovering later.

   A THEME CANNOT WRITE TO A CUSTOMER RECORD. Persisting a wishlist on Shopify's side means
   a customer metafield, and the only writes Shopify accepts are:
     - Admin API           — needs a private token, which cannot ship in a theme
     - Customer Account API `metafieldsSet` — exists since API 2024-07, but needs an APP
                             holding customer_write_customers plus approved protected
                             customer data access
     - App Proxy           — again, an app, plus HMAC verification on a server you own
   There is no app on this store. So there is no theme-only path to server storage, and any
   code here claiming to sync to an account would be pretending.

   What a theme CAN guarantee, and what this does guarantee, is that the list is correct for
   every identity on this device:

     guest        ->  c4u:wish:guest
     customer 42  ->  c4u:wish:c42

   One key per identity, which buys three things a single shared key does not:
     1. Logging in MERGES what you saved as a guest into your account list, then empties the
        guest key — so hearts pressed before signing in are not lost, and are not left behind
        to reappear for the next guest.
     2. Logging out leaves your account list untouched. Log back in and it is still there.
     3. Two people sharing a laptop never see each other's list.

   Cross-DEVICE sync is the one thing missing, and it is missing because of the API limit
   above, not because of how this is written. When an app or proxy does arrive, `pull` and
   `push` at the bottom are the only two functions that change; everything above them, and
   every caller, stays as it is.
   ================================================================================= */
(function(){
  var NS      = 'c4u:wish:';
  var LEGACY  = 'c4u_wish';     /* the first version: a bare array of handles, no snapshot */
  var MAX     = 200;            /* a guard, not a product rule — a runaway loop cannot fill the quota */

  /* localStorage throws, it does not merely return null: Safari's private mode and a browser
     set to block site data both raise on the first setItem. Everything routes through these
     two so one failure degrades to an in-memory list that works for the length of the visit,
     instead of taking the heart button down with it. */
  var mem = {};
  var ok  = true;
  function readKey(k){
    if(!ok) return mem[k] || null;
    try{ return localStorage.getItem(k); }catch(e){ ok = false; return mem[k] || null; }
  }
  function writeKey(k, v){
    mem[k] = v;
    if(!ok) return;
    try{ localStorage.setItem(k, v); }catch(e){ ok = false; }
  }
  function dropKey(k){
    delete mem[k];
    if(!ok) return;
    try{ localStorage.removeItem(k); }catch(e){ ok = false; }
  }

  function parse(raw){
    if(!raw) return [];
    var v;
    try{ v = JSON.parse(raw); }catch(e){ return []; }
    if(!v || !v.length) return [];
    /* tolerate the legacy shape (a plain string per item) wherever it turns up, so an old
       key never renders as a row of blank cards */
    var out = [];
    for(var i = 0; i < v.length; i++){
      var e = v[i];
      if(typeof e === 'string') e = { h: e };
      if(e && e.h) out.push(e);
    }
    return out;
  }

  function idOf(){
    var c = window.__c4uCustomer;
    return (c && String(c)) ? 'c' + c : 'guest';
  }
  function keyFor(id){ return NS + id; }

  var me  = idOf();
  var KEY = keyFor(me);

  function load(k){ return parse(readKey(k)); }
  function save(k, list){ writeKey(k, JSON.stringify(list.slice(0, MAX))); }

  /* union by handle, oldest `at` wins the position and the richer snapshot wins the data —
     a guest entry saved from a card carries less than one saved from a product page */
  function merge(a, b){
    var out = a.slice(), seen = {};
    for(var i = 0; i < out.length; i++) seen[out[i].h] = i;
    for(var j = 0; j < b.length; j++){
      var e = b[j], at = seen[e.h];
      if(at === undefined){ out.push(e); seen[e.h] = out.length - 1; }
      else {
        var cur = out[at];
        out[at] = {
          h: cur.h,
          t: cur.t || e.t, p: (cur.p == null ? e.p : cur.p), u: cur.u || e.u,
          i: cur.i || e.i, b: cur.b || e.b, c: (cur.c && cur.c.length ? cur.c : e.c),
          m: (cur.m == null ? e.m : cur.m), v: (cur.v && cur.v.length ? cur.v : e.v),
          at: Math.min(cur.at || 0, e.at || 0) || cur.at || e.at
        };
      }
    }
    return out;
  }

  /* ---- one-time housekeeping, in order ------------------------------------------------ */

  /* 1. the pre-namespace key, from the version that stored handles only. Its items land on
        the GUEST list even for a signed-in visitor, because it was written before there was
        any notion of who was holding it — and step 2 then folds it into their account. */
  var legacy = readKey(LEGACY);
  if(legacy){
    var conv = parse(legacy);
    if(conv.length) save(keyFor('guest'), merge(load(keyFor('guest')), conv));
    dropKey(LEGACY);
  }

  /* 2. THE LOGIN SWITCH. Anything hearted while signed out is folded into the account list
        the moment the account appears, and the guest key is emptied so it does not surface
        again for whoever browses next on this device. */
  if(me !== 'guest'){
    var guest = load(keyFor('guest'));
    if(guest.length){
      save(KEY, merge(load(KEY), guest));
      dropKey(keyFor('guest'));
    }
  }

  /* ---- the store ---------------------------------------------------------------------- */

  var subs = [];
  /* One subscriber must not be able to take the others down — a card that fails to draw should
     not stop the heart from repainting. But the error is REPORTED, not swallowed: a silent
     catch here already cost one debugging session, because a subscriber that threw on its very
     first call looked exactly like a wishlist that was empty. */
  function fire(fn, l){
    try{ fn(l); }
    catch(e){ if(window.console && console.error) console.error('[candyWish] subscriber failed:', e); }
  }
  function notify(){
    var l = list();
    for(var i = 0; i < subs.length; i++) fire(subs[i], l);
  }

  function list(){ return load(KEY); }
  function handles(){ return list().map(function(e){ return e.h; }); }
  function has(h){
    var l = list();
    for(var i = 0; i < l.length; i++) if(l[i].h === h) return true;
    return false;
  }
  function add(entry){
    if(!entry || !entry.h || has(entry.h)) return false;
    var l = list();
    entry.at = entry.at || Date.now();
    l.unshift(entry);                     /* newest first — the wishlist page reads top-down */
    save(KEY, l);
    notify();
    return true;
  }
  function remove(h){
    var l = list(), out = l.filter(function(e){ return e.h !== h; });
    if(out.length === l.length) return false;
    save(KEY, out);
    notify();
    return true;
  }
  function toggle(entry){
    if(!entry || !entry.h) return false;
    if(has(entry.h)){ remove(entry.h); return false; }
    add(entry);
    return true;
  }
  /* PATCH IN PLACE — the wishlist page's revalidation needs this. Doing it as remove()+add()
     would unshift the item back to the front, so a page that quietly refreshed four stale
     prices would also silently reorder itself under the reader. This keeps the position and
     only writes when something actually differs, so a revalidation pass that finds everything
     current fires no notify and causes no redraw. */
  function update(h, patch){
    var l = list(), hit = false;
    for(var i = 0; i < l.length; i++){
      if(l[i].h !== h) continue;
      for(var k in patch){
        if(!Object.prototype.hasOwnProperty.call(patch, k)) continue;
        if(JSON.stringify(l[i][k]) === JSON.stringify(patch[k])) continue;
        l[i][k] = patch[k];
        hit = true;
      }
      break;
    }
    if(!hit) return false;
    save(KEY, l);
    notify();
    return true;
  }

  /* ANOTHER TAB IS THE SAME PERSON. Without this, hearting something in one tab leaves every
     other open tab showing the old state until it is reloaded — and on a phone, "the other
     tab" is usually the one the shopper comes back to. */
  window.addEventListener('storage', function(e){
    if(e.key === KEY) notify();
  });

  window.candyWish = {
    list: list,
    handles: handles,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    update: update,
    count: function(){ return list().length; },
    /* subscribe; called immediately with the current list so a caller has one code path
       for "draw now" and "redraw on change" */
    on: function(fn){ if(typeof fn === 'function'){ subs.push(fn); fire(fn, list()); } },
    off: function(fn){ subs = subs.filter(function(f){ return f !== fn; }); },
    identity: function(){ return me; },
    persistent: function(){ return ok; },

    /* THE SEAM FOR SERVER SYNC. See the header. When this store gets an app or an app proxy,
       these two are the whole job: `pull` fetches the account's list and merges it in, `push`
       sends the local one. Nothing else in this file, and no caller anywhere, has to change —
       which is the reason the read/write path was funnelled through save() and notify()
       rather than letting each caller touch localStorage itself. */
    pull: function(){ return Promise.resolve(list()); },
    push: function(){ return Promise.resolve(true); }
  };

  /* a class on <html> so CSS can react to "this visitor has saved something" without JS
     having to find and touch every element that cares */
  function stamp(){
    var el = document.documentElement;
    el.classList.toggle('c4u-has-wish', list().length > 0);
  }
  subs.push(stamp);
  stamp();
})();
